import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { STATUSES, TIME_SLOTS, resolveIcon } from "../../constants.js";
import { toISO, fromISO, formatDayShort } from "../../utils/date.js";

export default function ItemModal({ open, onClose, onSave, initial, defaultDate, allDays, legForDate, categories, noAutoFocus = false }) {
  const defaultCatId = categories[0]?.id || "idea";
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(defaultCatId);
  const [status, setStatus] = useState("idea");
  const [timeSlot, setTimeSlot] = useState("anytime");
  const [date, setDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [link, setLink] = useState("");
  const [location, setLocation] = useState("");

  useEffect(() => {
    if (open) {
      if (initial) {
        setTitle(initial.title || "");
        setCategory(initial.category || defaultCatId);
        setStatus(initial.status || "idea");
        setTimeSlot(initial.timeSlot || "anytime");
        setDate(initial.date || defaultDate);
        setEndDate(initial.endDate || "");
        setNotes(initial.notes || "");
        setLink(initial.link || "");
        setLocation(initial.location || "");
      } else {
        setTitle("");
        setCategory(defaultCatId);
        setStatus("idea");
        setTimeSlot("anytime");
        setDate(defaultDate);
        setEndDate("");
        setNotes("");
        setLink("");
        setLocation("");
      }
    }
  }, [open, initial, defaultDate, defaultCatId]);

  if (!open) return null;

  const submit = () => {
    if (!title.trim()) return;
    const cleanEnd = endDate && endDate > date ? endDate : "";
    let cleanLink = link.trim();
    if (cleanLink && !/^https?:\/\//i.test(cleanLink) && !/^mailto:/i.test(cleanLink)) {
      cleanLink = "https://" + cleanLink;
    }
    onSave({ title: title.trim(), category, status, timeSlot, date, endDate: cleanEnd, notes: notes.trim(), link: cleanLink, location: location.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full sm:max-w-lg bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: "'Fraunces', Georgia, serif", maxHeight: "92dvh" }}
      >
        <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            {initial ? "Edit item" : "New item"}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
            <X size={18} />
          </button>
        </div>

        <div
          className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4"
          style={{ fontFamily: "system-ui, sans-serif", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Title</label>
            <input
              autoFocus={!noAutoFocus}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Borough Market lunch"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Start day</label>
              <select
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  if (endDate && endDate <= e.target.value) setEndDate("");
                }}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 text-sm"
              >
                {allDays.map((d) => {
                  const leg = legForDate(d);
                  return (
                    <option key={toISO(d)} value={toISO(d)}>
                      {formatDayShort(d)} — {leg ? `${leg.emoji} ${leg.city}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">
                End day <span className="normal-case tracking-normal text-slate-400 dark:text-slate-500">(optional)</span>
              </label>
              <select
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 text-sm"
              >
                <option value="">Single day</option>
                {allDays.filter((d) => toISO(d) > date).map((d) => {
                  const leg = legForDate(d);
                  return (
                    <option key={toISO(d)} value={toISO(d)}>
                      {formatDayShort(d)} — {leg ? `${leg.emoji} ${leg.city}` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
          {endDate && endDate > date && (
            <div className="-mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              {(() => {
                const days = Math.round((fromISO(endDate) - fromISO(date)) / 86400000) + 1;
                return `Spans ${days} days`;
              })()}
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => {
                const Icon = resolveIcon(c.icon);
                const active = c.id === category;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setCategory(c.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all"
                    style={active
                      ? { background: c.colour, color: "white", borderColor: c.colour }
                      : { background: `${c.colour}10`, color: c.colour, borderColor: `${c.colour}30` }}
                  >
                    <Icon size={13} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Status</label>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => {
                const active = s.id === status;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStatus(s.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${active ? "" : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600"}`}
                    style={active
                      ? { background: s.dot, color: "white", borderColor: s.dot }
                      : {}}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: active ? "white" : s.dot }} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Time of day</label>
            <div className="flex flex-wrap gap-1.5">
              {TIME_SLOTS.map((t) => {
                const active = t.id === timeSlot;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTimeSlot(t.id)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500"}`}
                  >
                    {t.label}{t.range && <span className="opacity-60 ml-1">{t.range}</span>}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Booking ref, address, etc."
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700 resize-none"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">
              Location <span className="normal-case tracking-normal text-slate-400 dark:text-slate-500">(optional)</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. 8 Stoney St, London SE1 9AA"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"
            />
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">
              Link <span className="normal-case tracking-normal text-slate-400 dark:text-slate-500">(optional)</span>
            </label>
            <input
              type="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="instagram.com/..., maps.google.com/..., etc."
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"
            />
          </div>
        </div>

        <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end flex-shrink-0">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!title.trim()}
            className="px-5 py-2 rounded-full bg-slate-900 dark:bg-slate-600 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {initial ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
